import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { readConsent, saveConsent, clearConsent, GRANTED, DENIED } from '../utils/consent';
import { GA_MEASUREMENT_ID } from '../utils/analytics';
import '../styles/Policy.css';

const STORE_EMAIL = 'celestialstones33@gmail.com';

export default function CookiePolicy() {
  const [choice, setChoice] = useState(() => readConsent());

  useEffect(() => {
    const onChange = (e) => setChoice(e.detail);
    window.addEventListener('cs-consent', onChange);
    return () => window.removeEventListener('cs-consent', onChange);
  }, []);

  const change = (value) => {
    saveConsent(value);
    setChoice(value);
    /* Deliberately not calling initAnalytics() on "allow" here: the tag
       loads on the next page view instead. Turning tracking off cannot
       unload a script that is already running, so a reload is the only
       honest way to make "off" mean off. */
  };

  return (
    <section className="policy-page">
      <div className="policy-container">
        <header className="policy-head">
          <h1>Cookie Policy</h1>
          <p className="policy-updated">Last updated: 24 September 2026</p>
        </header>

        <div className="policy-body">
          <p className="policy-lead">
            This page lists everything Celestial Stones stores in your
            browser, what each item is for, and how to change your mind.
          </p>

          <h2>The short version</h2>
          <p>
            We set <strong>no advertising cookies</strong> and we sell nothing
            to data brokers. The only optional item is Google Analytics, which
            tells us which stones people look at so we know what to stock. If
            you decline it, nothing about your visit is sent to Google.
          </p>

          <h2>What we store, and why</h2>

          <div className="policy-table">
            <div className="policy-tr policy-th">
              <span>What</span>
              <span>Why</span>
              <span>Needs consent?</span>
            </div>

            <div className="policy-tr">
              <span><code>cs_cart_v1</code></span>
              <span>The stones in your bag, so they survive a refresh.</span>
              <span>No — you asked for it</span>
            </div>
            <div className="policy-tr">
              <span><code>cs_coupon_v1</code></span>
              <span>A discount code you applied, held with your bag.</span>
              <span>No — you asked for it</span>
            </div>
            <div className="policy-tr">
              <span><code>cs_cookie_consent_v1</code></span>
              <span>Your answer to the cookie banner, so we stop asking.</span>
              <span>No — it records your choice</span>
            </div>
            <div className="policy-tr">
              <span>Supabase session</span>
              <span>Keeps you signed in to your account between visits.</span>
              <span>No — only set if you sign in</span>
            </div>
            <div className="policy-tr">
              <span>
                Google Analytics
                <br />
                <code>_ga</code>, <code>_ga_*</code>
              </span>
              <span>
                Anonymous statistics: pages viewed, stones opened, searches
                that found nothing. Property {GA_MEASUREMENT_ID}.
              </span>
              <span>
                <strong>Yes — optional</strong>
              </span>
            </div>
          </div>

          <p className="policy-note">
            The first four are not cookies at all — they are browser
            localStorage, which never travels to our server and is readable
            only by this site on this device.
          </p>

          <h2>Your choice</h2>

          <div className="consent-state">
            <p>
              Analytics is currently{' '}
              <strong>
                {choice === GRANTED
                  ? 'allowed'
                  : choice === DENIED
                    ? 'declined'
                    : 'not yet chosen'}
              </strong>
              .
            </p>

            <div className="consent-actions">
              <button
                className="policy-btn"
                onClick={() => change(GRANTED)}
                disabled={choice === GRANTED}
              >
                Allow analytics
              </button>
              <button
                className="policy-btn"
                onClick={() => change(DENIED)}
                disabled={choice === DENIED}
              >
                Decline analytics
              </button>
              {choice !== null && (
                <button className="policy-btn is-ghost" onClick={clearConsent}>
                  Ask me again
                </button>
              )}
            </div>

            <p className="policy-note">
              Switching analytics off takes effect on your next page load — a
              script already running can't be unloaded, and we would rather say
              so than pretend otherwise. To clear cookies Google has already
              set, use your browser's "clear site data" for this site.
            </p>
          </div>

          <h2>Doing it in your browser instead</h2>
          <p>
            Every major browser can block or delete cookies for a single site
            from its settings, and most offer a private window that discards
            everything on close. Those controls override anything on this page.
          </p>

          <h2>Questions</h2>
          <p>
            Write to{' '}
            <a href={`mailto:${STORE_EMAIL}`}>{STORE_EMAIL}</a> and we'll
            answer. See also our <Link to="/privacy-policy">Privacy Policy</Link>{' '}
            and <Link to="/data-request">Data &amp; Privacy Requests</Link>.
          </p>
        </div>
      </div>
    </section>
  );
}
